import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { McpAvailableToolToken, McpKeyToken } from '@sleed/bizcommerce-api';
import { mcpKeysApi } from '~/lib/api/client';
import { formToCreateRequest, formToUpdateRequest, type McpKeyFormValues } from '~/lib/schemas/mcp-key';

export const mcpKeyKeys = {
	all: ['mcp-keys'] as const,
	list: () => [...mcpKeyKeys.all, 'list'] as const,
	tools: () => [...mcpKeyKeys.all, 'available-tools'] as const
};

export function useMcpKeysList() {
	return useQuery<McpKeyToken[]>({
		queryKey: mcpKeyKeys.list(),
		queryFn: async () => (await mcpKeysApi.getAll()).data.items ?? []
	});
}

/** The ADMIN-registry tool inventory (name/risk/cardinality/requiredPermissions) for the grant picker. */
export function useMcpAvailableTools() {
	return useQuery<McpAvailableToolToken[]>({
		queryKey: mcpKeyKeys.tools(),
		queryFn: async () => (await mcpKeysApi.availableTools()).data.items ?? [],
		staleTime: 10 * 60 * 1000
	});
}

export function useMcpKeyMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: mcpKeyKeys.list() });

	/** Returns the minted response — the one-time `token` must be shown to the user once. */
	const create = useMutation({
		mutationFn: async (input: { form: McpKeyFormValues; riskByTool: Record<string, string | undefined> }) =>
			(await mcpKeysApi.insert(formToCreateRequest(input.form, input.riskByTool))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (input: { form: McpKeyFormValues; riskByTool: Record<string, string | undefined> }) =>
			(await mcpKeysApi.update(formToUpdateRequest(input.form, input.riskByTool), input.form.id)).data,
		onSuccess: invalidate
	});
	/** Rotates the secret — also returns a one-time `token`. */
	const regenerate = useMutation({
		mutationFn: async (id: number) => (await mcpKeysApi.regenerate(id)).data,
		onSuccess: invalidate
	});
	const revoke = useMutation({ mutationFn: async (id: number) => (await mcpKeysApi.revoke(id)).data, onSuccess: invalidate });
	const remove = useMutation({ mutationFn: async (id: number) => (await mcpKeysApi.remove(id)).data, onSuccess: invalidate });

	return { create, update, regenerate, revoke, remove };
}
